import { useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  toApiError,
  unwrap,
  type ApiError,
  type ExportKind,
  type ExportOut,
} from "../api/client";
import { t } from "../i18n/t";
import { exportKindLabel } from "../lib/exports";
import { routes } from "../lib/routes";
import { ErrorMessage, LiveMessage } from "./ui";

export function ExportButtons({
  orgId,
  nr,
  schoolId,
  schoolName,
  disabled = false,
  onQueued,
}: {
  orgId: string;
  nr: string;
  schoolId?: string;
  schoolName?: string;
  disabled?: boolean;
  onQueued?: (item: ExportOut) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [message, setMessage] = useState("");
  async function order(kind: ExportKind) {
    setBusy(true);
    setError(null);
    setMessage("");
    try {
      const item = await unwrap(
        api.POST("/api/orgs/{org_id}/kommuner/{nummer}/exports", {
          params: { path: { org_id: orgId, nummer: nr } },
          body: { kind, ...(schoolId ? { school_id: schoolId } : {}) },
        }),
      );
      onQueued?.(item);
      setMessage(t("exports.ordered", { kind: exportKindLabel(kind) }));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div>
      <span className="link-group">
        {(["pdf", "xlsx"] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            className="btn btn-secondary btn-small"
            disabled={disabled || busy}
            aria-label={
              schoolName
                ? t(kind === "pdf" ? "exports.pdfFor" : "exports.xlsxFor", { school: schoolName })
                : undefined
            }
            onClick={() => void order(kind)}
          >
            {t(
              schoolId
                ? kind === "pdf"
                  ? "exports.pdfShort"
                  : "exports.xlsxShort"
                : kind === "pdf"
                  ? "exports.pdf"
                  : "exports.xlsx",
            )}
          </button>
        ))}
      </span>
      <LiveMessage message={message} />
      {message && !onQueued && (
        <Link to={routes.exports(orgId, nr)}>{t("exports.goToExports")}</Link>
      )}
      {error && <ErrorMessage error={error} />}
    </div>
  );
}
